/**
 * InviteModal Component
 * Modal for sending user invitations with resizable email field
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { usersService, type InvitationData, type User } from '../../services/users.service';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { isHr, ROLE_LABELS } from '../../lib/roles';
import type { Role } from '../../types';

export interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: User[];
}

export const InviteModal = ({ isOpen, onClose, users }: InviteModalProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const [role, setRole] = useState<Role>('user');
  const [emailMessage, setEmailMessage] = useState('');

  // Only HR managers may invite someone as HR (§1.5); admins see Admin/User only.
  const roleOptions = [
    { value: 'user', label: ROLE_LABELS.user },
    { value: 'admin', label: ROLE_LABELS.admin },
    ...(isHr(currentUser?.role) ? [{ value: 'hr', label: ROLE_LABELS.hr }] : []),
  ];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRole('user');
      setEmailMessage(getDefaultEmailMessage());
    }
  }, [isOpen]);

  const getDefaultEmailMessage = () => {
    return `Hello,

You've been invited to join Team Wizard! This platform will help you stay connected with your team and access important information.

Click the link below to create your account and get started.

Best regards,
The Team`;
  };

  // Send invitations mutation
  const sendMutation = useMutation({
    mutationFn: (invitations: InvitationData[]) => usersService.sendInvitations(invitations),
    onSuccess: (response) => {
      console.log('🎯 onSuccess CALLED with response:', response);

      // Validate response structure
      if (!response || !response.summary) {
        toast.error('Invalid response from server');
        console.error('Invalid invitation response:', response);
        onClose(); // Close modal even on validation error
        return;
      }

      const { summary } = response;

      if (summary.success > 0) {
        toast.success(
          `${summary.success} invitation${summary.success !== 1 ? 's' : ''} sent successfully`
        );
      }

      if (summary.failed > 0) {
        toast.error(
          `${summary.failed} invitation${summary.failed !== 1 ? 's' : ''} failed to send`
        );
      }

      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (error: Error) => {
      console.log('🎯 onError CALLED with error:', error);
      toast.error(error.message || 'Failed to send invitations');
    },
    onSettled: () => {
      console.log('🎯 onSettled CALLED - mutation completed');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that we have users to invite
    if (users.length === 0) {
      toast.error('No users selected for invitation');
      return;
    }

    // Build invitations array
    const invitations: InvitationData[] = users.map((user) => ({
      email: user.email,
      member_id: user.member_id!,
      role: role,
      email_message: emailMessage,
    }));

    sendMutation.mutate(invitations);
  };

  const isLoading = sendMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={users.length === 1 ? 'Send Invitation' : `Send Invitations (${users.length})`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Selected Users Display */}
        <div className="bg-surface-app rounded-lg p-4">
          <h4 className="text-sm font-medium text-body mb-2">
            Inviting {users.length} user{users.length !== 1 ? 's' : ''}:
          </h4>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {users.map((user) => (
              <div key={user.id} className="text-sm text-meta">
                • {user.first_name && user.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user.email}{' '}
                ({user.email})
              </div>
            ))}
          </div>
        </div>

        {/* Role Selection */}
        <Select
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          options={roleOptions}
        />

        {/* Email Message */}
        <div>
          <label htmlFor="email-message" className="block text-sm font-medium text-body mb-1">
            Email Message
          </label>
          <textarea
            id="email-message"
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 border border-strong rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-heading bg-surface"
            style={{ minHeight: '120px', maxHeight: '400px', resize: 'none' }}
            placeholder="Enter the invitation message..."
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Send Invitation{users.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
